import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { CONSENT_EVENT, CONSENT_KEY, CONSENT_NOTICE, SETTINGS_EVENT, lookupRegion, readConsent, saveConsent, syncGoogleConsent, type ConsentRegion } from "@/lib/advertising-consent";

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [region, setRegion] = useState<ConsentRegion>("unknown");
  const [error, setError] = useState("");

  useEffect(() => {
    let disposed = false;
    let currentRegion: ConsentRegion = "unknown";
    const sync = () => syncGoogleConsent(readConsent(), currentRegion);
    const show = () => setVisible(true);
    const onStorage = (event: StorageEvent) => {
      if (event.key === CONSENT_KEY || event.key === null) sync();
    };
    window.addEventListener(CONSENT_EVENT, sync);
    window.addEventListener(SETTINGS_EVENT, show);
    window.addEventListener("storage", onStorage);
    void lookupRegion().then((resolved) => {
      if (disposed) return;
      currentRegion = resolved;
      setRegion(resolved);
      const choice = readConsent();
      setVisible(choice === null && resolved !== "ordinary");
      syncGoogleConsent(choice, resolved);
    });
    return () => {
      disposed = true;
      window.removeEventListener(CONSENT_EVENT, sync);
      window.removeEventListener(SETTINGS_EVENT, show);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setChoice = (choice: "accepted" | "declined") => {
    if (!saveConsent(choice, region)) {
      setError("Your browser could not save this choice. Optional tracking remains blocked until your choice can be saved.");
      return;
    }
    setError("");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-4xl rounded-lg border border-border bg-card/95 p-4 shadow-lg backdrop-blur sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            {CONSENT_NOTICE} See our{" "}
            <Link to="/cookies" className="text-brand-text underline underline-offset-2">
              Cookie Policy
            </Link>{" "}
            for details.
            {error && <span role="alert" className="block mt-2 text-destructive">{error}</span>}
          </p>
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" size="sm" onClick={() => setChoice("declined")}>
              Decline
            </Button>
            <Button size="sm" onClick={() => setChoice("accepted")}>
              Accept
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
